import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import DoubleClickEditableTextField, {
  EmptyStringCompletionPolicy,
} from 'ui-library/reactflow/nodes/DoubleClickEditableTextField';

import {createHandleIdForMethod} from './handleIdentifierUtils';
import {MethodNameViewer} from './MethodNameViewer';
import {MAX_METHOD_NAME_LENGTH} from './ProcedureDesignConstants';
import {ProcedureNodeHandle} from './ProcedureHandles';
import {ProcedureMethodEditorViewButton} from './ProcedureMethodEditorViewButton';
import {nodeNameBoxClass} from './procedureMethodNodeCommonConsts';
import ProcedureMethodSettingsDropdown from './ProcedureMethodSettingsDropdown';
import {MethodCanvasNodeData} from './ProcedureReactFlowCanvasTypes';
import {MethodNameEditingState} from './useMethodNameEditing';

export interface MethodNodeNameSectionProps {
  methodCid: string;
  data: MethodCanvasNodeData;
  hasFullAccess: boolean;
  isProcedureReadOnly: boolean;
  hasValidationErrors: boolean;
  nameEditing: MethodNameEditingState;
}

export function MethodNodeNameSection(props: MethodNodeNameSectionProps): React.JSX.Element {
  const {methodCid, data, hasFullAccess, isProcedureReadOnly, hasValidationErrors, nameEditing} = props;
  const {isEditingName, setIsEditingName, editingNameError, getDefaultNameWhenEmpty, onDraftNameChanged} =
    nameEditing;

  return (
    <>
      <div
        className={nodeNameBoxClass(hasValidationErrors, Boolean(editingNameError))}
        {...getTestProps({component: 'MethodNodeNameSection', element: 'nameBox'})}
      >
        {!isProcedureReadOnly && hasFullAccess ? (
          <DoubleClickEditableTextField
            onComplete={(changedValue: string) => {
              data.onChangeMethodName(methodCid, changedValue.trim());
            }}
            viewer={<MethodNameViewer methodCid={methodCid} name={data.name} isHoverable={true} />}
            isEditing={isEditingName}
            setIsEditing={setIsEditingName}
            value={data.name}
            emptyStringCompletionPolicy={EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE}
            defaultValue={getDefaultNameWhenEmpty}
            onDraftNameChanged={onDraftNameChanged}
            onCancel={() => {
              if (data.name.trim().length === 0) {
                data.onChangeMethodName(methodCid, getDefaultNameWhenEmpty());
              }
            }}
            isError={Boolean(editingNameError)}
            maxValueLength={MAX_METHOD_NAME_LENGTH}
            placeholderText="Enter a name"
          />
        ) : (
          <MethodNameViewer methodCid={methodCid} name={data.name} isHoverable={false} />
        )}
        {!isEditingName && !isProcedureReadOnly && hasFullAccess && (
          <ProcedureMethodSettingsDropdown
            methodCid={methodCid}
            onDuplicateClick={data.onDuplicateClick}
            onEditMethodSelection={data.onEditMethod}
            onDeleteMethod={data.onDeleteMethod}
          />
        )}
        {isProcedureReadOnly && hasFullAccess && (
          <ProcedureMethodEditorViewButton methodCid={methodCid} onViewMethodEditor={data.onEditMethod} />
        )}
        <ProcedureNodeHandle
          type="source"
          id={createHandleIdForMethod(methodCid, 'source')}
          isVisible={false}
        />
        <ProcedureNodeHandle
          type="target"
          id={createHandleIdForMethod(methodCid, 'target')}
          isVisible={false}
        />
      </div>
      {editingNameError && (
        <div
          {...getTestProps({component: 'MethodNodeNameSection', element: 'errorWrapper'})}
          className={css(styles.errorWrapper)}
        >
          <FieldError message={editingNameError} />
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  errorWrapper: {
    padding: '0 9px 2px',
    borderLeft: `1px solid ${GRAY_DARK}`,
    borderRight: `1px solid ${GRAY_DARK}`,
    marginTop: '-2px',
  },
});
